import React, {
	forwardRef,
	useEffect,
	useMemo,
	useRef,
	useState,
	type PropsWithChildren,
} from 'react';
import clamp from 'lodash/clamp';
import { Box, xcss } from '@atlaskit/primitives';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { START_NODE_ID } from '@atlassian/jira-project-configuration-commons/src/model/constants.tsx';
import {
	DEFAULT_PAN,
	DEFAULT_ZOOM_LEVEL,
	MAXIMUM_ZOOM_LEVEL,
	MINIMUM_ZOOM_LEVEL,
} from '../../common/constants/diagram.tsx';
import {
	type DiagramStatus,
	type DiagramTransition,
	type Point,
	LayerIndex,
	TransitionIcons,
	type StatefulDiagramDirectTransition,
	type StatefulDiagramSharedTransition,
	type StatefulDiagramGlobalLoopedTransition,
} from '../../common/types/diagram.tsx';
import type { Positionable, ItemReference, ViewBoxRect } from '../../common/types/index.tsx';
import TransitionLabel from '../../common/ui/diagram-transition-label/index.tsx';
import { useAutoLayout } from '../../common/utils/use-auto-layout/index.tsx';
import { useCanvasDragDropEvents } from '../../common/utils/use-canvas-drag-drop/index.tsx';
import { useCanvasView } from '../../common/utils/use-canvas-view/index.tsx';
import { useControllable } from '../../common/utils/use-controllable/index.tsx';
import { useItemDragDrop } from '../../common/utils/use-item-drag-drop/index.tsx';
import { useLatest } from '../../common/utils/use-latest/index.tsx';
import { useOnWheel } from '../../common/utils/use-on-wheel/index.tsx';
import { usePortDragAndDrop } from '../../common/utils/use-ports-drag-drop/index.tsx';
import { usePorts } from '../../common/utils/use-ports/index.tsx';
import { useRecordState } from '../../common/utils/use-record-state/index.tsx';
import { useSnapLines } from '../../common/utils/use-snap-lines/index.tsx';
import { useStartNode } from '../../common/utils/use-start-node/index.tsx';
import { useStatefulStatuses } from '../../common/utils/use-stateful-statuses/index.tsx';
import { useStatefulTransitions } from '../../common/utils/use-stateful-transitions/index.tsx';
import { useTransitionDragDrop } from '../../common/utils/use-transition-drag-drop/index.tsx';
import { useTransitionProposals } from '../../common/utils/use-transition-proposals/index.tsx';
import { useTransitionRerouting } from '../../common/utils/use-transition-rerouting/index.tsx';
import { useGlobalLoopsContainer } from '../../common/utils/use-global-loops-container/index.tsx';
import { DIAGRAM_CONTAINER_TYPE } from '../../common/constants/index.tsx';
import { CanvasSvg } from './canvas/index.tsx';
import DirectTransition from './direct-transition/index.tsx';
import GlobalLoopsContainer from './global-loops-container/index.tsx';
import GlobalTransition from './global-transition/index.tsx';
import { LayerGroup, LayerItem } from './layers/index.tsx';
import Port from './port/index.tsx';
import ProposedTransition from './proposed-transition/index.tsx';
import SharedTransition from './shared-transition/index.tsx';
import SnapLines from './snap-lines/index.tsx';
import StartNode from './start-node/index.tsx';
import Status from './status/index.tsx';
import {
	applyTransitionIconConditions,
	getStartNodeLayer,
	getStatusLayer,
	getTransitionLayer,
	getTransitionSource,
} from './utils.tsx';
import ViewOverlay from './view-overlay/index.tsx';

type Props = {
	isImmutable?: boolean;
	zoom?: number;
	pan?: Point;
	selectedItem?: ItemReference;
	targetedItem?: ItemReference;
	movingItem?: Positionable;
	startNodePosition: Point | undefined;
	globalLoopsContainerPosition?: Point;
	transitions: DiagramTransition[];
	statuses: DiagramStatus[];
	collapseTransitionLabels?: boolean;
	shouldShowGlobalLoops?: boolean;
	onClearGlobalLoopsContainerPosition: () => void;
	onRenderComplete?: () => void;
	onSelect?: (item: ItemReference | undefined) => void;
	onItemMove?: (payload: Positionable) => void;
	onTransitionCreate?: (transition: DiagramTransition) => void;
	onTransitionPortChange?: (transition: DiagramTransition) => void;
	onViewBoxChange?: (viewBox: ViewBoxRect) => void;
	onZoomChange?: (zoom: number) => void;
	onPanChange?: (pan: Point) => void;
};

const useStatusWidthsWithReadinessCheck = (statuses: DiagramStatus[]) => {
	const [statusWidths, setStatusWidths, setStatusWidthEntry] = useRecordState<string, number>();

	return useMemo(() => {
		const isReady = statuses.length === Object.keys(statusWidths).length;
		return [statusWidths, setStatusWidths, setStatusWidthEntry, isReady] as const;
	}, [setStatusWidthEntry, setStatusWidths, statusWidths, statuses.length]);
};

const WorkflowDiagram = ({
	isImmutable = false,
	pan: controlledPan,
	zoom: controlledZoom,
	startNodePosition,
	globalLoopsContainerPosition: providedGlobalLoopsContainerPosition,
	onClearGlobalLoopsContainerPosition,
	transitions,
	statuses,
	selectedItem,
	targetedItem,
	movingItem,
	onSelect,
	onItemMove,
	onTransitionCreate,
	onTransitionPortChange,
	onZoomChange,
	onPanChange,
	onViewBoxChange,
	onRenderComplete,
	collapseTransitionLabels = false,
	shouldShowGlobalLoops = false,
}: Props) => {
	const rootSvgRef = useRef(null);
	const [hoveredItem, setHoveredItem] = useState<ItemReference>();
	const [statusWidthsOld, , setStatusWidthEntryOld] = useRecordState<string, number>();
	const [statusWidthsNew, , setStatusWidthEntryNew, isStatusWidthsReady] =
		useStatusWidthsWithReadinessCheck(statuses);
	const statusWidths = fg('concurrent-rendering-workflows-replace-settimeout')
		? statusWidthsNew
		: statusWidthsOld;
	const setStatusWidthEntry = fg('concurrent-rendering-workflows-replace-settimeout')
		? setStatusWidthEntryNew
		: setStatusWidthEntryOld;

	const [pan, updatePan] = useControllable(controlledPan, onPanChange, DEFAULT_PAN);

	const [zoom, updateZoom] = useControllable(
		controlledZoom ? clamp(controlledZoom, MINIMUM_ZOOM_LEVEL, MAXIMUM_ZOOM_LEVEL) : undefined,
		onZoomChange,
		DEFAULT_ZOOM_LEVEL,
	);

	const { canvasContainerRef, diagramSize, viewBox } = useCanvasView(zoom, pan, onViewBoxChange);

	const { dragDropState, temporaryPan } = useCanvasDragDropEvents({
		rootSvgRef,
		zoom,
		pan,
		viewBox,
		isImmutable,
		updatePan,
		onSelect,
	});

	const [
		reroutingTransition,
		{ onTransitionReroutingCancel, onTransitionReroutingClose, onTransitionReroutingMove },
	] = useTransitionRerouting();

	const [
		proposedTransition,
		{ onTransitionProposalCancel, onTransitionProposalMove, onTransitionProposalClose },
	] = useTransitionProposals();

	const {
		isLayoutStable,
		defaultPositions,
		defaultGlobalLoopsContainerPosition,
		transitionsWithDefaultPorts,
	} = useAutoLayout({
		rootSvgRef,
		canvasContainerRef,
		startNodePosition,
		transitions,
		statuses,
		statusWidths,
		isStatusWidthsReady,
		providedGlobalLoopsContainerPosition,
		onClearGlobalLoopsContainerPosition,
		onItemMove,
		onTransitionPortChange,
		onPan: updatePan,
		onZoom: updateZoom,
	});

	const latestOnRenderComplete = useLatest(onRenderComplete);
	useEffect(() => {
		if (isLayoutStable) {
			latestOnRenderComplete.current?.();
		}
	}, [isLayoutStable, latestOnRenderComplete]);

	const { snapLines, snappedSubjectPosition } = useSnapLines({
		isImmutable,
		startNodePosition,
		globalLoopsContainerPosition: providedGlobalLoopsContainerPosition,
		statuses,
		transitions,
		statusWidths,
		movingItem,
		dragDropState,
	});

	const { draggingItem } = useItemDragDrop({
		isImmutable,
		dragDropState,
		snappedSubjectPosition,
		onItemMove,
	});

	const globalLoopsContainer = useGlobalLoopsContainer({
		providedGlobalLoopsContainerPosition,
		defaultGlobalLoopsContainerPosition,
		targetedItem,
		draggingItem,
		movingItem,
	});

	const startNode = useStartNode({
		startNodePosition,
		targetedItem,
		hoveredItem,
		movingItem,
		draggingItem,
		defaultPositions,
	});

	const statefulStatuses = useStatefulStatuses({
		statuses,
		selectedItem,
		targetedItem,
		hoveredItem,
		draggingItem,
		movingItem,
		defaultPositions,
	});

	const { ports, visiblePorts } = usePorts({
		hoveredItem,
		selectedItem,
		startNodePosition: startNode.position,
		transitions,
		statuses: statefulStatuses,
		statusWidths,
		defaultPositions,
		dragDropState,
		reroutingTransition,
		proposedTransition,
		isImmutable,
	});

	const statefulTransitions = useStatefulTransitions({
		transitions,
		selectedItem,
		targetedItem,
		hoveredItem,
		ports,
		reroutingTransition,
		transitionsWithDefaultPorts,
	});

	const onWheel = useOnWheel({
		pan,
		zoom,
		updateZoom,
		updatePan,
	});

	usePortDragAndDrop({
		ports,
		isImmutable,
		selectedItem,
		transitions,
		dragDropState,
		onTransitionReroutingMove,
		onTransitionProposalMove,
	});

	useTransitionDragDrop({
		ports,
		dragDropState,
		proposedTransition,
		reroutingTransition,
		transitions,
		onTransitionProposalCancel,
		onTransitionReroutingCancel,
		onTransitionCreate,
		onTransitionPortChange,
	});

	useEffect(() => {
		const containerRef = canvasContainerRef?.current;

		if (containerRef) {
			// @ts-expect-error - WheelEvent is the correct value
			containerRef.addEventListener('wheel', onWheel, { passive: false });
		}

		return () => {
			if (containerRef) {
				// @ts-expect-error - WheelEvent is the correct value
				containerRef.removeEventListener('wheel', onWheel);
			}
		};
	}, [canvasContainerRef, onWheel]);

	return (
		<DiagramContainer
			ref={canvasContainerRef}
			isContentHidden={!isLayoutStable}
			isTargeted={targetedItem?.type === DIAGRAM_CONTAINER_TYPE}
		>
			<CanvasSvg
				rootSvgRef={rootSvgRef}
				pan={temporaryPan || pan}
				zoom={zoom}
				diagramSize={diagramSize}
			>
				<LayerGroup>
					{shouldShowGlobalLoops && globalLoopsContainer.position && (
						<LayerItem layer={LayerIndex.GlobalLoopsContainer}>
							<GlobalLoopsContainer
								isImmutable={isImmutable}
								position={globalLoopsContainer.position}
								transitions={statefulTransitions.filter(
									(x): x is StatefulDiagramGlobalLoopedTransition => x.type === 'global-looped',
								)}
								onSelect={onSelect}
								onHover={setHoveredItem}
								isMoving={globalLoopsContainer.isMoving}
								isTargeted={globalLoopsContainer.isTargeted}
							/>
						</LayerItem>
					)}

					{/* Transition paths */}
					{statefulTransitions.map((transition) => (
						<LayerItem
							key={`transitionPath_${transition.id}`}
							layer={getTransitionLayer(transition)}
						>
							{transition.type === 'global' && (
								<GlobalTransition
									id={transition.id}
									flag={transition.flag}
									isHovered={transition.isHovered}
									isImmutable={isImmutable}
									isSelected={transition.isSelected}
									isTargeted={transition.isTargeted}
									isHighlighted={transition.isHighlighted}
									portPosition={transition.targetPort?.position}
									onSelect={onSelect}
									onHover={setHoveredItem}
									icons={applyTransitionIconConditions(transition.icons, undefined)}
								/>
							)}
							{transition.type === 'direct' && (
								<DirectTransition
									id={transition.id}
									path={transition.path}
									flag={transition.flag}
									isImmutable={isImmutable}
									isSelected={transition.isSelected}
									isTargeted={transition.isTargeted}
									isHighlighted={transition.isHighlighted}
									isHovered={transition.isHovered}
									start={transition.sourcePort}
									end={transition.targetPort}
									onSelect={onSelect}
									onHover={setHoveredItem}
								/>
							)}
							{transition.type === 'shared' && (
								<SharedTransition
									id={transition.id}
									flag={transition.flag}
									isHovered={transition.isHovered}
									isImmutable={isImmutable}
									isSelected={transition.isSelected}
									isTargeted={transition.isTargeted}
									isHighlighted={transition.isHighlighted}
									connections={transition.connections}
									onSelect={onSelect}
									onHover={setHoveredItem}
								/>
							)}
						</LayerItem>
					))}

					{/* Transition labels */}
					{statefulTransitions
						.filter(
							(
								transition,
							): transition is
								| StatefulDiagramDirectTransition
								| StatefulDiagramSharedTransition => {
								if (transition.type === 'global') {
									return false;
								}

								if (transition.type === 'global-looped') {
									return false;
								}

								return true;
							},
						)
						.map((transition) => (
							<LayerItem
								key={`transitionLabel_${transition.id}`}
								layer={getTransitionLayer(transition)}
							>
								{transition.type === 'shared' ? (
									<>
										{transition.connections?.map((connection, i) => (
											<TransitionLabel
												id={transition.id}
												key={i}
												label={transition.label}
												icons={applyTransitionIconConditions(
													transition.icons,
													connection.sourcePort.statusId,
												).concat(TransitionIcons.SharedTransition)}
												flag={transition.flag}
												position={connection.labelPosition}
												isImmutable={isImmutable}
												isHovered={transition.isHovered}
												isSelected={transition.isSelected}
												isTargeted={transition.isTargeted}
												isHighlighted={transition.isHighlighted}
												isOverlapping={transition.isOverlapping}
												isOverlapHovered={transition.isOverlapHovered}
												onSelect={onSelect}
												onHover={setHoveredItem}
												collapseTransitionLabels={collapseTransitionLabels}
												connection={
													fg('hix-7062_shared_transition_show_label_highlighted')
														? connection
														: undefined
												}
												hoveredItem={
													fg('hix-7062_shared_transition_show_label_highlighted')
														? hoveredItem
														: undefined
												}
											/>
										))}
									</>
								) : (
									<TransitionLabel
										id={transition.id}
										label={transition.label}
										icons={applyTransitionIconConditions(
											transition.icons,
											getTransitionSource(transition),
										)}
										flag={transition.flag}
										position={transition.labelPosition}
										isImmutable={isImmutable}
										isHovered={transition.isHovered}
										isSelected={transition.isSelected}
										isTargeted={transition.isTargeted}
										isHighlighted={transition.isHighlighted}
										isOverlapping={transition.isOverlapping}
										isOverlapHovered={transition.isOverlapHovered}
										onSelect={onSelect}
										onHover={setHoveredItem}
										collapseTransitionLabels={collapseTransitionLabels}
										connection={
											fg('hix-7062_shared_transition_show_label_highlighted')
												? {
														sourcePort: transition.sourcePort,
														targetPort: transition.targetPort,
													}
												: undefined
										}
										hoveredItem={
											fg('hix-7062_shared_transition_show_label_highlighted')
												? hoveredItem
												: undefined
										}
									/>
								)}
							</LayerItem>
						))}

					{statefulStatuses.map((status) => (
						<LayerItem key={`status_${status.id}`} layer={getStatusLayer(status)}>
							<Status
								id={status.id}
								label={status.label}
								position={status.position}
								category={status.category}
								flag={status.flag}
								isImmutable={isImmutable}
								isSelected={status.isSelected}
								isTargeted={status.isTargeted}
								isMoving={status.isMoving}
								onHover={setHoveredItem}
								onStatusResize={setStatusWidthEntry}
								onSelect={onSelect}
							/>
						</LayerItem>
					))}

					<LayerItem key={START_NODE_ID} layer={getStartNodeLayer(startNode)}>
						<StartNode
							id={START_NODE_ID}
							position={startNode.position}
							isImmutable={isImmutable}
							isTargeted={startNode.isTargeted}
							isMoving={startNode.isMoving}
							onHover={setHoveredItem}
						/>
					</LayerItem>

					<LayerItem layer={LayerIndex.Proposals}>
						<SnapLines lines={snapLines} />
					</LayerItem>

					{visiblePorts.map((port) => (
						<LayerItem key={`port_${port.key}`} layer={LayerIndex.Ports}>
							<Port
								portKey={port.key}
								isImmutable={isImmutable}
								position={port.position}
								isGlowing={port.isGlowing}
								parentStatusId={port.statusId}
								portIndex={port.portIndex}
								onHover={setHoveredItem}
								isProposingTransition={proposedTransition.stage !== 'none'}
								isRerouting={reroutingTransition.stage !== 'none'}
								onTransitionReroutingClose={onTransitionReroutingClose}
								onTransitionProposalClose={onTransitionProposalClose}
							/>
						</LayerItem>
					))}

					{proposedTransition.stage !== 'none' && (
						<LayerItem layer={LayerIndex.Proposals}>
							<ProposedTransition
								start={proposedTransition.line.start}
								end={proposedTransition.line.end}
							/>
							<Port isImmutable position={proposedTransition.line.end} />
						</LayerItem>
					)}
					{(movingItem || draggingItem) && (
						<LayerItem layer={LayerIndex.PrioritisedTransitions}>
							<ViewOverlay viewBox={viewBox} />
						</LayerItem>
					)}
				</LayerGroup>
			</CanvasSvg>
		</DiagramContainer>
	);
};

export default WorkflowDiagram;

type ContainerProps = PropsWithChildren<{ isContentHidden: boolean; isTargeted: boolean }>;

const DiagramContainer = forwardRef<HTMLDivElement, ContainerProps>(
	({ isContentHidden, isTargeted, children }, ref) => (
		<Box
			ref={ref}
			xcss={[
				containerBaseStyles,
				isContentHidden && containerHiddenStyles,
				isTargeted && containerTargetedStyles,
			]}
		>
			{children}
		</Box>
	),
);

const containerBaseStyles = xcss({
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	minHeight: `${42 * gridSize}px`,
	width: '100%',
	// disables browser handling of all touch events, giving us full control
	touchAction: 'none',
});

const containerHiddenStyles = xcss({
	opacity: '0',
});

const containerTargetedStyles = xcss({
	outline: '2px solid',
	outlineColor: 'color.border.focused',
});
